import type { RelatorioSaboresProducaoItem } from "../../../services/producaoService";
import { CHART_PALETTE } from "../../../constants";

export const SLICE_PALETTE = [...CHART_PALETTE];

export type NamedValue = { name: string; value: number; fullName: string };

function truncateLabel(s: string, max: number): string {
  const t = s.trim();
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
}

export function aggregateByRotulo(
  linhas: RelatorioSaboresProducaoItem[],
  topN?: number
): NamedValue[] {
  const map = new Map<string, number>();
  for (const r of linhas) {
    map.set(r.rotulo, (map.get(r.rotulo) ?? 0) + r.quantidade);
  }
  const sorted = Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  const cap = topN == null ? sorted.length : Math.min(topN, sorted.length);
  const top = sorted.slice(0, cap);
  const restSum = sorted.slice(cap).reduce((acc, [, v]) => acc + v, 0);
  const out: NamedValue[] = top.map(([fullName, value]) => ({
    name: truncateLabel(fullName, 36),
    value,
    fullName,
  }));
  if (restSum > 0) {
    out.push({
      name: "Outros",
      value: restSum,
      fullName: "Demais combinações de sabor/tamanho",
    });
  }
  return out;
}

export function aggregateByLoja(
  linhas: RelatorioSaboresProducaoItem[]
): NamedValue[] {
  const map = new Map<string, number>();
  for (const r of linhas) {
    map.set(r.lojaNome, (map.get(r.lojaNome) ?? 0) + r.quantidade);
  }
  return Array.from(map.entries())
    .map(([fullName, value]) => ({
      name: truncateLabel(fullName, 28),
      value,
      fullName,
    }))
    .sort((a, b) => b.value - a.value);
}

export function lojasDistintasCount(
  linhas: RelatorioSaboresProducaoItem[]
): number {
  return new Set(linhas.map((r) => r.lojaId)).size;
}

export type TopSaborNoPeriodo = {
  rank: number;
  saborId: number;
  saborNome: string;
  quantidade: number;
};

function rankSaboresAgregados(
  linhas: RelatorioSaboresProducaoItem[],
  order: "asc" | "desc",
  limit: number
): TopSaborNoPeriodo[] {
  const map = new Map<number, { nome: string; q: number }>();
  for (const r of linhas) {
    const cur = map.get(r.saborId) ?? { nome: r.saborNome, q: 0 };
    cur.q += r.quantidade;
    map.set(r.saborId, cur);
  }
  const rows = Array.from(map.entries()).map(([saborId, v]) => ({
    saborId,
    saborNome: v.nome,
    quantidade: v.q,
  }));
  rows.sort((a, b) => {
    const d =
      order === "desc"
        ? b.quantidade - a.quantidade
        : a.quantidade - b.quantidade;
    if (d !== 0) return d;
    return a.saborNome.localeCompare(b.saborNome, "pt-BR");
  });
  return rows.slice(0, limit).map((row, i) => ({ rank: i + 1, ...row }));
}

export function buildTop10SaboresAgregados(
  linhas: RelatorioSaboresProducaoItem[]
): TopSaborNoPeriodo[] {
  return rankSaboresAgregados(linhas, "desc", 10);
}

export function buildBottom10SaboresAgregados(
  linhas: RelatorioSaboresProducaoItem[]
): TopSaborNoPeriodo[] {
  return rankSaboresAgregados(linhas, "asc", 10);
}
